/**
 * Full Option Chain Table — CE | Strike | PE with ATM and max pain highlighted
 */
import React, { useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Chip
} from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

interface ChainRow {
  strike: number;
  spotPrice?: number;
  ceOi?: number;
  ceOiChange?: number;
  ceIv?: number;
  ceDelta?: number;
  ceLtp?: number;
  peOi?: number;
  peOiChange?: number;
  peIv?: number;
  peDelta?: number;
  peLtp?: number;
}

interface Props {
  data: ChainRow[];
  spotPrice: number;
  maxPainStrike?: number;
}

const fmt = (v: any, dec = 0) => (v != null && v !== 0) ? Number(v).toFixed(dec) : '—';
const fmtK = (v: any) => v ? `${(Number(v) / 1000).toFixed(1)}K` : '—';

const OptionChainTable: React.FC<Props> = ({ data, spotPrice, maxPainStrike }) => {
  const { C } = useAppTheme();

  const oiChangeColor = (v: any) => {
    if (!v) return 'text.secondary';
    return Number(v) > 0 ? C.bull : C.bear;
  };

  const sorted = useMemo(() =>
    [...data].sort((a, b) => Number(b.strike) - Number(a.strike)),
    [data]
  );

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Option Chain</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Chip size="small" label="ATM" sx={{ bgcolor: C.atmBg, color: C.atm, fontSize: '0.65rem' }} />
        <Chip size="small" label="Max Pain" sx={{ bgcolor: C.accentMuted, color: C.maxPain, fontSize: '0.65rem' }} />
        <Chip size="small" sx={{ bgcolor: C.bearMuted, color: C.bear, fontSize: '0.65rem' }} label="■ CE (Resistance)" />
        <Chip size="small" sx={{ bgcolor: C.bullMuted, color: C.bull, fontSize: '0.65rem' }} label="■ PE (Support)" />
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {/* CE side — Resistance */}
              <ColHeader label="OI" align="right" color={C.bear} />
              <ColHeader label="Chg" align="right" color={C.bear} />
              <ColHeader label="IV%" align="right" color={C.bear} />
              <ColHeader label="Delta" align="right" color={C.bear} />
              <ColHeader label="LTP" align="right" color={C.bear} />
              {/* Strike */}
              <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: 'background.paper', py: 0.5 }}>
                STRIKE
              </TableCell>
              {/* PE side — Support */}
              <ColHeader label="LTP" align="left" color={C.bull} />
              <ColHeader label="Delta" align="left" color={C.bull} />
              <ColHeader label="IV%" align="left" color={C.bull} />
              <ColHeader label="Chg" align="left" color={C.bull} />
              <ColHeader label="OI" align="left" color={C.bull} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => {
              const strike = Number(row.strike);
              const isAtm = Math.abs(strike - spotPrice) <= 25;
              const isMaxPain = maxPainStrike && Math.abs(strike - maxPainStrike) < 1;
              const rowBg = isAtm ? C.atmBg : isMaxPain ? C.accentMuted : undefined;

              return (
                <TableRow key={strike} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: 'action.hover' } }}>
                  {/* CE columns */}
                  <TableCell align="right" sx={{ fontSize: '0.72rem', color: C.callText, py: 0.4, px: 1 }}>{fmtK(row.ceOi)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.72rem', color: oiChangeColor(row.ceOiChange), py: 0.4, px: 1 }}>
                    {row.ceOiChange ? (Number(row.ceOiChange) > 0 ? '+' : '') + fmtK(row.ceOiChange) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.72rem', color: C.callText, py: 0.4, px: 1 }}>{fmt(row.ceIv, 1)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.72rem', color: C.callText, py: 0.4, px: 1 }}>{fmt(row.ceDelta, 3)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.72rem', color: C.callText, py: 0.4, px: 1, fontWeight: 700 }}>₹{fmt(row.ceLtp, 1)}</TableCell>
                  {/* Strike */}
                  <TableCell align="center" sx={{
                    fontWeight: isAtm ? 900 : 700,
                    fontSize: isAtm ? '0.85rem' : '0.78rem',
                    color: isAtm ? C.atm : isMaxPain ? C.maxPain : 'text.primary',
                    py: 0.5
                  }}>
                    {strike.toLocaleString('en-IN')}
                    {isAtm && <Typography variant="caption" sx={{ display: 'block', color: C.atm, fontSize: '0.6rem' }}>ATM</Typography>}
                  </TableCell>
                  {/* PE columns */}
                  <TableCell align="left" sx={{ fontSize: '0.72rem', color: C.putText, py: 0.4, px: 1, fontWeight: 700 }}>₹{fmt(row.peLtp, 1)}</TableCell>
                  <TableCell align="left" sx={{ fontSize: '0.72rem', color: C.putText, py: 0.4, px: 1 }}>{fmt(row.peDelta, 3)}</TableCell>
                  <TableCell align="left" sx={{ fontSize: '0.72rem', color: C.putText, py: 0.4, px: 1 }}>{fmt(row.peIv, 1)}</TableCell>
                  <TableCell align="left" sx={{ fontSize: '0.72rem', color: oiChangeColor(row.peOiChange), py: 0.4, px: 1 }}>
                    {row.peOiChange ? (Number(row.peOiChange) > 0 ? '+' : '') + fmtK(row.peOiChange) : '—'}
                  </TableCell>
                  <TableCell align="left" sx={{ fontSize: '0.72rem', color: C.putText, py: 0.4, px: 1 }}>{fmtK(row.peOi)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ColHeader: React.FC<{ label: string; align: 'left' | 'right'; color: string }> = ({ label, align, color }) => (
  <TableCell align={align} sx={{ fontWeight: 700, fontSize: '0.68rem', color, py: 0.5, bgcolor: 'background.paper' }}>
    {label}
  </TableCell>
);

export default OptionChainTable;
